// Mushroom noise bank (ElevenLabs SFX cached in object storage): preload + play. Non-verbal only.
import { getAudioContext, getMaster, decodeQuiet } from '@/spore/eerieAudio';
import { cachedAudio, cachedJson, withTimeout } from './showCache';

export function createSfxBank() {
  const clips = new Map();
  let bankId = null;
  let available = 0;
  let loaded = 0;
  let failed = 0;
  let bg = null;
  let layer = null;
  let lastOneShot = 0;

  async function fetchClip(c) {
    for (let i = 0; i < 3; i += 1) {
      try {
        c.buffer = await withTimeout(decodeQuiet((await cachedAudio(c.audio_path, 15000)).buf), 15000);
        loaded += 1;
        return;
      } catch {
        if (i < 2) await new Promise((res) => setTimeout(res, 800 * 2 ** i));
      }
    }
    failed += 1;
    c.failed = true;
  }

  async function load(doc = null) {
    let d = doc;
    if (!d) {
      try {
        d = (await cachedJson('/sfx/bank/current', 15000)).doc; // saved copy first
      } catch {
        d = null;
      }
    }
    if (!d) return false;
    bankId = d.bank_id;
    clips.clear();
    loaded = 0;
    failed = 0;
    d.clips.forEach((c) => clips.set(c.id, { ...c }));
    const q = [...clips.values()].filter((c) => c.audio_path);
    available = q.length;
    await Promise.all(Array.from({ length: 6 }, async () => {
      while (q.length) await fetchClip(q.shift());
    }));
    return true;
  }

  function play(id, { gain = 0.8, loop = false, rate = 1 } = {}) {
    const c = clips.get(id);
    const ctx = getAudioContext();
    if (!c || !c.buffer || !ctx) return null;
    const src = ctx.createBufferSource();
    src.buffer = c.buffer;
    src.loop = loop;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(getMaster());
    src.start();
    return { src, g, ends: ctx.currentTime + (loop ? 1e9 : c.buffer.duration / rate) };
  }
  const pick = (prefix, n) => `${prefix}_${1 + Math.floor(Math.random() * n)}`;
  const busy = (h) => h && getAudioContext() && getAudioContext().currentTime < h.ends - 0.15;

  return {
    load,
    progress: () => ({ sfx: loaded, sfxAvailable: available, sfxFailed: failed, bankId }),
    retryFailed: () => [...clips.values()].filter((c) => c.failed && !c.buffer).forEach((c) => {
      c.failed = false;
      failed -= 1;
      fetchClip(c);
    }),
    has: (id) => !!(clips.get(id) && clips.get(id).buffer),
    // first available id wins (new SPORE SAYS clip, else an older bank clip)
    one(ids, opts) {
      const id = ids.find((x) => clips.get(x) && clips.get(x).buffer);
      return id ? play(id, opts) : null;
    },
    fadeStop(h, s = 0.12) {
      const ctx = getAudioContext();
      if (!h || !ctx) return;
      h.g.gain.setTargetAtTime(0, ctx.currentTime, s);
      h.src.stop(ctx.currentTime + s * 5);
    },
    background(on) {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (on && !bg) bg = play(pick('bg', 2), { gain: 0, loop: true });
      if (bg) bg.g.gain.setTargetAtTime(on ? 0.22 : 0.05, ctx.currentTime, 0.6);
    },
    // continuous moaning bed that follows the activity envelope (front of mix during TRY)
    bed(level, live) {
      const ctx = getAudioContext();
      if (!ctx) return;
      if (!bg) bg = play(pick('bg', 2), { gain: 0, loop: true });
      if (bg) bg.g.gain.setTargetAtTime(live ? 0.06 + 1.1 * level : 0.05, ctx.currentTime, 0.25);
    },
    touch(kind) {
      const now = performance.now();
      if (now - lastOneShot < 250) return;
      lastOneShot = now;
      play(kind === 'poke' ? pick('squeak', 3) : pick('sigh', 3), { gain: kind === 'poke' ? 0.7 : 0.55, rate: 0.9 + Math.random() * 0.2 });
    },
    intensity(feeling, excitation, band = null) {
      if (!feeling || busy(layer)) return;
      const lvl = band || (excitation < 40 ? 'low' : excitation < 75 ? 'med' : 'high');
      layer = play(`${feeling}_${lvl}`, { gain: 0.6 + excitation / 160, rate: 0.95 + Math.random() * 0.1 });
    },
    climax(feeling) {
      if (layer) layer.src.stop();
      layer = play(`${feeling}_climax`, { gain: 1.25 });
      return !!layer;
    },
    stopAll() {
      [bg, layer].forEach((h) => h && h.src.stop());
      bg = null;
      layer = null;
    },
  };
}
